import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Tooltip as AlchemyTooltip } from '@m/alchemy-ui/Tooltip';
import { type Challenge, type Maybe } from '../../../common/core-api';
import { useFile } from '../../../hooks';
import { removeTags } from '../../../helpers';
import { NotificationCardTypes } from '../../../common/enums';
import { RelativeDateWithTooltip, ResponsiveImage, Tooltip } from '../../../common/components';
import { NOTIF_STREAM_DEFAULT_IMAGE_HEIGHT } from '../../shared-constants';
import * as Styled from './ChallengeContent.styled';
import { Footer } from '.';

export const ChallengeContent = ({
  challenge,
  time,
  isValid
}: {
  readonly challenge?: Maybe<Challenge>;
  readonly time?: string;
  readonly isValid: boolean;
}) => {
  const [getFile] = useFile();
  const intl = useIntl();

  const { id, files, title, description } = challenge || {};
  const challengeImage = files?.list;

  const [challengeImageUrl, setChallengeImageUrl] = useState('');
  const [width, setWidth] = useState(0);
  const [isTruncated, setIsTruncated] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const truncatedDescription = removeTags(description || '') as string;

  useEffect(() => {
    const getAuthUrl = async () => {
      let url = '';

      if (challengeImage) {
        url = await getFile({ file: challengeImage, width, height: NOTIF_STREAM_DEFAULT_IMAGE_HEIGHT });
      }

      setChallengeImageUrl(url || '');
    };

    getAuthUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeImage, width]);

  useEffect(() => {
    if (descriptionRef.current) {
      const element = descriptionRef.current;
      const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);
      const maxHeight = lineHeight * 3;
      const actualHeight = element.scrollHeight;

      setIsTruncated(actualHeight > maxHeight);
    }
  }, [truncatedDescription]);

  const imageResizeCallback = (width: number) => {
    if (!challengeImage) {
      return '';
    }

    setWidth(width);
    return challengeImageUrl;
  };

  if (!challenge) {
    return null;
  }

  const message = intl.formatMessage({
    id: 'youNoLongerHaveAccessToThisItem',
    defaultMessage: 'You no longer have access to this item'
  });

  const clearTitle = removeTags(title || '');

  return (
    <Styled.ContentContainer>
      <Styled.ChallengeHeader>
        {isValid ? (
          <Styled.ChallengeTitle href={`/category/${id}`} target="_blank" rel="noreferrer">
            {clearTitle}
          </Styled.ChallengeTitle>
        ) : (
          <Tooltip content={message}>
            <Styled.ChallengeTitleGreyout aria-label={`${clearTitle}. ${message}`} aria-disabled="true" tabIndex={0}>
              {clearTitle}
            </Styled.ChallengeTitleGreyout>
          </Tooltip>
        )}
        {time && <RelativeDateWithTooltip time={time} />}
      </Styled.ChallengeHeader>
      <Styled.ChallengeDescription>
        <div className="shortDescription" aria-hidden="true" ref={descriptionRef}>
          {truncatedDescription}
        </div>
        {isTruncated && (
          <AlchemyTooltip
            trigger={
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              <span aria-label="Show More Description" tabIndex={0}>
                <FormattedMessage id="showMore" defaultMessage="Show more" />
              </span>
            }
            placement="auto"
            contents={truncatedDescription}
          />
        )}
      </Styled.ChallengeDescription>
      {challengeImage && (
        <Styled.ChallengeImageWrapper>
          <ResponsiveImage callback={imageResizeCallback} altText={title || ''} />
        </Styled.ChallengeImageWrapper>
      )}
      {isValid && <Footer challenge={challenge} type={NotificationCardTypes.CHALLENGE} />}
    </Styled.ContentContainer>
  );
};
